import * as joint from '@clientio/rappid'
import AceLayer from '../rappid/ace-layer'
import * as customRouters from './custom-routers'

export default class RappidContext {
  paper!: joint.dia.Paper

  paperScroller!: joint.ui.PaperScroller

  graph!: joint.dia.Graph

  create(this: RappidContext): void {
    this.createGraph()
    this.createPaper()
    this.createPaperScroller()
    new joint.ui.Tooltip({
      target: '[data-tooltip]',
      padding: 10,
      direction: joint.ui.Tooltip.TooltipArrowPosition.Auto,
      rootTarget: document.body,
    })
    //For debugging:
    //window.graph = this.graph
  }

  mount(this: RappidContext, canvas: HTMLDivElement): void {
    canvas.appendChild((this.paperScroller as any).el)

    // Center and zoom the graph.
    // For some event loop issue we didn't figure out a timeout of 0ms is needed for this to work.
    setTimeout(() => {
      this.paperScroller.zoomToFit({
        padding: { left: 20, top: 20, right: 20, bottom: 20 },
        maxScale: 1,
      })
    }, 0)

    const aceLayer = new AceLayer(this)
    aceLayer.init()

    this.paper.unfreeze()
  }

  private createGraph(this: RappidContext) {
    this.graph = new joint.dia.Graph({}, { cellNamespace: joint.shapes })

    this.registerContainerResizing()
  }

  private registerContainerResizing() {
    this.graph.on('change:position', (cell: joint.dia.Cell, newPosition: any, opt: any) => {
      if (opt.skipParentHandler) return

      if (cell.prop('embeds') && cell.prop('embeds').length) {
        // If we're manipulating a parent element, let's store
        // it's original position to a special property so that
        // we can shrink the parent element back while manipulating
        // its children.
        cell.prop('originalPosition', cell.prop('position'))
      }

      let parentId = cell.prop('parent')
      cell.getEmbeddedCells().forEach((child) => {
        if (!child.get('hidden')) {
          while (parentId) {
            const parent = this.graph.getCell(parentId)
            parentId = parent.prop('parent')

            if (!parent.prop('originalPosition'))
              parent.prop('originalPosition', parent.prop('position'))

            const originalPosition = parent.prop('originalPosition')

            let newX = originalPosition.x
            let newY = originalPosition.y
            let newCornerX = originalPosition.x + 5
            let newCornerY = originalPosition.y + 5

            parent.getEmbeddedCells().forEach((child) => {
              const childBbox = child.getBBox()
              const inset = 5

              if (childBbox.x - inset < newX) {
                newX = childBbox.x - inset
              }
              if (childBbox.y - inset < newY) {
                newY = childBbox.y - inset
              }
              if (childBbox.corner().x + inset > newCornerX) {
                newCornerX = childBbox.corner().x + inset
              }
              if (childBbox.corner().y + inset > newCornerY) {
                newCornerY = childBbox.corner().y + inset
              }
            })

            // Note that we also pass a flag so that we know we shouldn't adjust the
            // `originalPosition` and `originalSize` in our handlers as a reaction
            // on the following `set()` call.
            parent.prop(
              {
                position: { x: newX, y: newY },
                size: { width: newCornerX - newX, height: newCornerY - newY },
              },
              { skipParentHandler: true },
            )
          }
        }
      })
    })
  }

  private createPaper(this: RappidContext) {
    this.paper = new joint.dia.Paper({
      model: this.graph,
      width: 800,
      height: 800,
      gridSize: 10,
      drawGrid: true,
      defaultConnector: {
        name: 'normal',
        args: {
          radius: 20,
        },
      },
      defaultAnchor: function (view, magnet, ref, opt, endType, linkView) {
        if (endType === 'source') {
          return joint.anchors.left(view, magnet, ref, opt, endType, linkView)
        } else {
          return joint.anchors.right(view, magnet, ref, opt, endType, linkView)
        }
      },
      routerNamespace: {
        ...joint.routers,
        ...customRouters,
      },
      interactive: function (cellView) {
        if (cellView.model.get('locked')) {
          return {
            elementMove: false,
          }
        }
        return true
      },
      background: {
        color: '#F2F2F2',
      },
      frozen: true,
      async: true,
      sorting: joint.dia.Paper.sorting.APPROX,
      linkPinning: true,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      allowLink(linkView: any, paper: any) {
        return linkView.getTargetElement() != null && linkView.getSourceElement() != null
      },
      cellViewNamespace: joint.shapes,
    })
  }

  private createPaperScroller() {
    this.paperScroller = new joint.ui.PaperScroller({
      paper: this.paper,
      autoResizePaper: true,
      borderless: true,
      scrollWhileDragging: true,
      cursor: 'grab',
    })
  }
}
